import {
  Bot,
  Calculator,
  Clock3,
  Diameter,
  Download,
  ChevronDown,
  Gauge,
  Layers,
  RulerDimensionLine,
  SlidersHorizontal,
  Upload,
  X,
  Zap
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { DepositionCalculator } from "./calculators/wireMaterial/DepositionCalculator";
import { EnergyCalculator } from "./calculators/energy/EnergyCalculator";
import { defaultEnergyInputs } from "./calculators/energy/presets";
import type { EnergyInputs } from "./calculators/energy/types";
import { GeometryCalculator } from "./calculators/geometry/GeometryCalculator";
import { calculateGeometry } from "./calculators/geometry/formulas";
import { defaultGeometryInputs } from "./calculators/geometry/presets";
import type { GeometryInputs, GeometryMode } from "./calculators/geometry/types";
import { SheetThicknessCalculator } from "./calculators/sheetThickness/SheetThicknessCalculator";
import { defaultSheetThicknessInputs } from "./calculators/sheetThickness/presets";
import type { SheetThicknessInputs } from "./calculators/sheetThickness/types";
import { TimeSpeedCalculator } from "./calculators/timeSpeed/TimeSpeedCalculator";
import { calculateTimeSpeed } from "./calculators/timeSpeed/formulas";
import { defaultTimeSpeedInputs } from "./calculators/timeSpeed/presets";
import type { TimeSpeedInputs, TimeSpeedMode } from "./calculators/timeSpeed/types";
import { WireMaterialCalculator } from "./calculators/wireMaterial/WireMaterialCalculator";
import { defaultWireMaterialInputs } from "./calculators/wireMaterial/presets";
import type { WireMaterialInputs } from "./calculators/wireMaterial/types";
import { WireConversionCalculator } from "./calculators/wireConversion/WireConversionCalculator";
import { defaultWireConversionInputs } from "./calculators/wireConversion/presets";
import type { WireConversionInputs } from "./calculators/wireConversion/types";
import { WeldParameterCalculator } from "./calculators/weldParameter/WeldParameterCalculator";
import { defaultInputs } from "./calculators/weldParameter/presets";
import type { CalculationMode, WeldParameterInputs } from "./calculators/weldParameter/types";
import { copy } from "./data/i18n";
import { fromDisplayValue, toDisplayValue } from "./data/units";
import type { Language, UnitSystem, VisualTheme } from "./types/preferences";

const STORAGE_KEY = "jules-tools:web:v1";
const MIN_GEOMETRY_ANGLE = 1;
const MAX_GEOMETRY_ANGLE = 179;

const navItems = [
  { key: "weld", icon: Calculator, enabled: true },
  { key: "wireConversion", icon: Diameter, enabled: true },
  { key: "geometry", icon: RulerDimensionLine, enabled: true },
  { key: "sheetThickness", icon: Layers, enabled: true },
  { key: "wireMaterial", icon: Diameter, enabled: true },
  { key: "deposition", icon: Gauge, enabled: true },
  { key: "timeSpeed", icon: Clock3, enabled: true },
  { key: "energy", icon: Zap, enabled: true },
  { key: "multiLayer", icon: Layers, enabled: false },
  { key: "robot", icon: Bot, enabled: false }
] as const;

type ModuleKey = (typeof navItems)[number]["key"];

type SavedState = {
  version: 1;
  activeModule: ModuleKey;
  language: Language;
  unitSystem: UnitSystem;
  visualTheme: VisualTheme;
  mode: CalculationMode;
  inputs: WeldParameterInputs;
  inputText: Record<keyof WeldParameterInputs, string>;
  wireConversionInputs: WireConversionInputs;
  wireConversionText: Record<keyof WireConversionInputs, string>;
  geometryMode: GeometryMode;
  geometryInputs: GeometryInputs;
  geometryText: Record<keyof GeometryInputs, string>;
  sheetThicknessInputs: SheetThicknessInputs;
  sheetThicknessText: Record<keyof SheetThicknessInputs, string>;
  wireMaterialInputs: WireMaterialInputs;
  wireMaterialText: Record<keyof WireMaterialInputs, string>;
  timeSpeedMode: TimeSpeedMode;
  timeSpeedInputs: TimeSpeedInputs;
  timeSpeedText: Record<keyof TimeSpeedInputs, string>;
  energyInputs: EnergyInputs;
  energyText: Record<keyof EnergyInputs, string>;
};

function isLanguage(value: unknown): value is Language {
  return value === "de" || value === "en";
}

function isUnitSystem(value: unknown): value is UnitSystem {
  return value === "metric" || value === "imperial";
}

function isVisualTheme(value: unknown): value is VisualTheme {
  return value === "jarvis" || value === "m3Light" || value === "m3Dark";
}

function normalizeVisualTheme(value: unknown): VisualTheme {
  if (isVisualTheme(value)) return value;
  if (value === "classic") return "m3Light";
  return "jarvis";
}

function isMode(value: unknown): value is CalculationMode {
  return value === "aMass" || value === "travelSpeed" || value === "wireFeed" || value === "wireDiameter";
}

function isModuleKey(value: unknown): value is ModuleKey {
  return navItems.some((item) => item.key === value && item.enabled);
}

function parseNumberInput(value: string) {
  const normalized = value.trim().replace(",", ".");
  if (normalized === "") return null;
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
}

function decimalSeparator(language: Language) {
  return language === "de" ? "," : ".";
}

function inputNumberText(value: number, digits: number, language: Language) {
  return String(Number(value.toFixed(digits))).replace(".", decimalSeparator(language));
}

function normalizeInputText(value: string, language: Language) {
  const separator = decimalSeparator(language);
  let hasSeparator = false;

  return [...value].reduce((text, character) => {
    if (/\d/.test(character)) return `${text}${character}`;
    if (character === "." || character === ",") {
      if (hasSeparator) return text;
      hasSeparator = true;
      return text === "" ? `0${separator}` : `${text}${separator}`;
    }
    return text;
  }, "");
}

function normalizeTextValues<T extends Record<string, string>>(values: Partial<T> | undefined, language: Language) {
  if (!values) return {};
  return Object.fromEntries(
    Object.entries(values).map(([key, value]) => [key, typeof value === "string" ? normalizeInputText(value, language) : value])
  ) as Partial<T>;
}

function clampGeometryAngle(value: number) {
  return Math.min(MAX_GEOMETRY_ANGLE, Math.max(MIN_GEOMETRY_ANGLE, value));
}

function normalizeGeometryInputs(inputs: GeometryInputs): GeometryInputs {
  return {
    ...inputs,
    angle: Number.isFinite(inputs.angle) ? clampGeometryAngle(inputs.angle) : defaultGeometryInputs.angle
  };
}

function weldInputText(key: keyof WeldParameterInputs, value: number, unitSystem: UnitSystem, language: Language) {
  if (key === "aMass") return inputNumberText(toDisplayValue("aMass", value, unitSystem), unitSystem === "metric" ? 2 : 3, language);
  if (key === "wireDiameter") {
    return inputNumberText(toDisplayValue("wireDiameter", value, unitSystem), unitSystem === "metric" ? 2 : 3, language);
  }
  if (key === "wireFeed") return inputNumberText(toDisplayValue("wireFeed", value, unitSystem), unitSystem === "metric" ? 2 : 1, language);
  return inputNumberText(toDisplayValue("travelSpeed", value, unitSystem), 1, language);
}

function textFromInputs(inputs: WeldParameterInputs, unitSystem: UnitSystem, language: Language) {
  return {
    aMass: weldInputText("aMass", inputs.aMass, unitSystem, language),
    wireDiameter: weldInputText("wireDiameter", inputs.wireDiameter, unitSystem, language),
    wireFeed: weldInputText("wireFeed", inputs.wireFeed, unitSystem, language),
    travelSpeed: weldInputText("travelSpeed", inputs.travelSpeed, unitSystem, language)
  };
}

function wireTextFromInputs(inputs: WireConversionInputs, unitSystem: UnitSystem, language: Language) {
  return {
    sourceDiameter: inputNumberText(toDisplayValue("wireDiameter", inputs.sourceDiameter, unitSystem), unitSystem === "metric" ? 2 : 3, language),
    sourceWireFeed: inputNumberText(toDisplayValue("wireFeed", inputs.sourceWireFeed, unitSystem), unitSystem === "metric" ? 2 : 1, language),
    targetDiameter: inputNumberText(toDisplayValue("wireDiameter", inputs.targetDiameter, unitSystem), unitSystem === "metric" ? 2 : 3, language)
  };
}

function geometryTextFromInputs(inputs: GeometryInputs, unitSystem: UnitSystem, language: Language) {
  return {
    aMass: inputNumberText(toDisplayValue("aMass", inputs.aMass, unitSystem), unitSystem === "metric" ? 2 : 3, language),
    zMeasure: inputNumberText(toDisplayValue("aMass", inputs.zMeasure, unitSystem), unitSystem === "metric" ? 2 : 3, language),
    angle: String(Number(inputs.angle.toFixed(0)))
  };
}

function sheetThicknessTextFromInputs(inputs: SheetThicknessInputs, unitSystem: UnitSystem, language: Language) {
  return {
    aMass: inputNumberText(toDisplayValue("aMass", inputs.aMass, unitSystem), unitSystem === "metric" ? 2 : 3, language)
  };
}

function displayWeightKg(value: number, unitSystem: UnitSystem) {
  return unitSystem === "metric" ? value : value * 2.2046226218;
}

function metricWeightKg(value: number, unitSystem: UnitSystem) {
  return unitSystem === "metric" ? value : value / 2.2046226218;
}

function displayLengthCm(value: number, unitSystem: UnitSystem) {
  return unitSystem === "metric" ? value : value / 2.54;
}

function metricLengthCm(value: number, unitSystem: UnitSystem) {
  return unitSystem === "metric" ? value : value * 2.54;
}

function wireMaterialTextFromInputs(inputs: WireMaterialInputs, unitSystem: UnitSystem, language: Language) {
  return {
    spoolWeight: inputNumberText(displayWeightKg(inputs.spoolWeight, unitSystem), 1, language),
    wireDiameter: inputNumberText(toDisplayValue("wireDiameter", inputs.wireDiameter, unitSystem), unitSystem === "metric" ? 2 : 3, language),
    density: inputNumberText(inputs.density, 2, language),
    wireFeed: inputNumberText(toDisplayValue("wireFeed", inputs.wireFeed, unitSystem), unitSystem === "metric" ? 2 : 1, language),
    weldingTime: inputNumberText(inputs.weldingTime, 1, language)
  };
}

function timeSpeedInputText(key: keyof TimeSpeedInputs, value: number, unitSystem: UnitSystem, language: Language) {
  if (key === "weldLength") return inputNumberText(displayLengthCm(value, unitSystem), unitSystem === "metric" ? 1 : 2, language);
  if (key === "duration") return inputNumberText(value, 1, language);
  return inputNumberText(toDisplayValue("travelSpeed", value, unitSystem), 1, language);
}

function timeSpeedTextFromInputs(inputs: TimeSpeedInputs, unitSystem: UnitSystem, language: Language) {
  return {
    weldLength: timeSpeedInputText("weldLength", inputs.weldLength, unitSystem, language),
    duration: timeSpeedInputText("duration", inputs.duration, unitSystem, language),
    travelSpeed: timeSpeedInputText("travelSpeed", inputs.travelSpeed, unitSystem, language)
  };
}

function energyTextFromInputs(inputs: EnergyInputs, unitSystem: UnitSystem, language: Language) {
  return {
    impulseEnergy: inputNumberText(inputs.impulseEnergy, 1, language),
    weldLength: inputNumberText(displayLengthCm(inputs.weldLength, unitSystem), unitSystem === "metric" ? 1 : 2, language),
    power: inputNumberText(inputs.power, 3, language),
    travelSpeed: inputNumberText(toDisplayValue("travelSpeed", inputs.travelSpeed, unitSystem), 1, language)
  };
}

function loadSavedState(): SavedState | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<SavedState>;

    if (!isLanguage(parsed.language) || !isUnitSystem(parsed.unitSystem) || !isMode(parsed.mode)) {
      return null;
    }

    const geometryInputs = normalizeGeometryInputs({ ...defaultGeometryInputs, ...parsed.geometryInputs });

    return {
      version: 1,
      activeModule: isModuleKey(parsed.activeModule) ? parsed.activeModule : "weld",
      language: parsed.language,
      unitSystem: parsed.unitSystem,
      visualTheme: normalizeVisualTheme(parsed.visualTheme),
      mode: parsed.mode,
      inputs: { ...defaultInputs, ...parsed.inputs },
      inputText: {
        ...textFromInputs({ ...defaultInputs, ...parsed.inputs }, parsed.unitSystem, parsed.language),
        ...normalizeTextValues<Record<keyof WeldParameterInputs, string>>(parsed.inputText, parsed.language)
      },
      wireConversionInputs: { ...defaultWireConversionInputs, ...parsed.wireConversionInputs },
      wireConversionText: {
        ...wireTextFromInputs({ ...defaultWireConversionInputs, ...parsed.wireConversionInputs }, parsed.unitSystem, parsed.language),
        ...normalizeTextValues<Record<keyof WireConversionInputs, string>>(parsed.wireConversionText, parsed.language)
      },
      geometryMode: parsed.geometryMode === "aMass" || parsed.geometryMode === "zMeasure" ? parsed.geometryMode : "zMeasure",
      geometryInputs,
      geometryText: {
        ...geometryTextFromInputs(geometryInputs, parsed.unitSystem, parsed.language),
        ...normalizeTextValues<Record<keyof GeometryInputs, string>>(parsed.geometryText, parsed.language),
        angle: String(Number(geometryInputs.angle.toFixed(0)))
      },
      sheetThicknessInputs: { ...defaultSheetThicknessInputs, ...parsed.sheetThicknessInputs },
      sheetThicknessText: {
        ...sheetThicknessTextFromInputs({ ...defaultSheetThicknessInputs, ...parsed.sheetThicknessInputs }, parsed.unitSystem, parsed.language),
        ...normalizeTextValues<Record<keyof SheetThicknessInputs, string>>(parsed.sheetThicknessText, parsed.language)
      },
      wireMaterialInputs: { ...defaultWireMaterialInputs, ...parsed.wireMaterialInputs },
      wireMaterialText: {
        ...wireMaterialTextFromInputs({ ...defaultWireMaterialInputs, ...parsed.wireMaterialInputs }, parsed.unitSystem, parsed.language),
        ...normalizeTextValues<Record<keyof WireMaterialInputs, string>>(parsed.wireMaterialText, parsed.language)
      },
      timeSpeedMode: parsed.timeSpeedMode === "duration" || parsed.timeSpeedMode === "travelSpeed" ? parsed.timeSpeedMode : "travelSpeed",
      timeSpeedInputs: { ...defaultTimeSpeedInputs, ...parsed.timeSpeedInputs },
      timeSpeedText: {
        ...timeSpeedTextFromInputs({ ...defaultTimeSpeedInputs, ...parsed.timeSpeedInputs }, parsed.unitSystem, parsed.language),
        ...normalizeTextValues<Record<keyof TimeSpeedInputs, string>>(parsed.timeSpeedText, parsed.language)
      },
      energyInputs: { ...defaultEnergyInputs, ...parsed.energyInputs },
      energyText: {
        ...energyTextFromInputs({ ...defaultEnergyInputs, ...parsed.energyInputs }, parsed.unitSystem, parsed.language),
        ...normalizeTextValues<Record<keyof EnergyInputs, string>>(parsed.energyText, parsed.language)
      }
    };
  } catch {
    return null;
  }
}

export function App() {
  const saved = loadSavedState();
  const [language, setLanguage] = useState<Language>(saved?.language ?? "de");
  const [unitSystem, setUnitSystemState] = useState<UnitSystem>(saved?.unitSystem ?? "metric");
  const [visualTheme, setVisualTheme] = useState<VisualTheme>(saved?.visualTheme ?? "jarvis");
  const [activeModule, setActiveModule] = useState<ModuleKey>(saved?.activeModule ?? "weld");
  const [mode, setMode] = useState<CalculationMode>(saved?.mode ?? "travelSpeed");
  const [inputs, setInputs] = useState<WeldParameterInputs>(saved?.inputs ?? defaultInputs);
  const [inputText, setInputText] = useState<Record<keyof WeldParameterInputs, string>>(
    saved?.inputText ?? textFromInputs(defaultInputs, saved?.unitSystem ?? "metric", saved?.language ?? "de")
  );
  const [wireConversionInputs, setWireConversionInputs] = useState<WireConversionInputs>(
    saved?.wireConversionInputs ?? defaultWireConversionInputs
  );
  const [wireConversionText, setWireConversionText] = useState<Record<keyof WireConversionInputs, string>>(
    saved?.wireConversionText ?? wireTextFromInputs(defaultWireConversionInputs, saved?.unitSystem ?? "metric", saved?.language ?? "de")
  );
  const [geometryInputs, setGeometryInputs] = useState<GeometryInputs>(saved?.geometryInputs ?? defaultGeometryInputs);
  const [geometryMode, setGeometryMode] = useState<GeometryMode>(saved?.geometryMode ?? "zMeasure");
  const [geometryText, setGeometryText] = useState<Record<keyof GeometryInputs, string>>(
    saved?.geometryText ?? geometryTextFromInputs(defaultGeometryInputs, saved?.unitSystem ?? "metric", saved?.language ?? "de")
  );
  const [sheetThicknessInputs, setSheetThicknessInputs] = useState<SheetThicknessInputs>(
    saved?.sheetThicknessInputs ?? defaultSheetThicknessInputs
  );
  const [sheetThicknessText, setSheetThicknessText] = useState<Record<keyof SheetThicknessInputs, string>>(
    saved?.sheetThicknessText ?? sheetThicknessTextFromInputs(defaultSheetThicknessInputs, saved?.unitSystem ?? "metric", saved?.language ?? "de")
  );
  const [wireMaterialInputs, setWireMaterialInputs] = useState<WireMaterialInputs>(
    saved?.wireMaterialInputs ?? defaultWireMaterialInputs
  );
  const [wireMaterialText, setWireMaterialText] = useState<Record<keyof WireMaterialInputs, string>>(
    saved?.wireMaterialText ?? wireMaterialTextFromInputs(defaultWireMaterialInputs, saved?.unitSystem ?? "metric", saved?.language ?? "de")
  );
  const [timeSpeedInputs, setTimeSpeedInputs] = useState<TimeSpeedInputs>(saved?.timeSpeedInputs ?? defaultTimeSpeedInputs);
  const [timeSpeedMode, setTimeSpeedMode] = useState<TimeSpeedMode>(saved?.timeSpeedMode ?? "travelSpeed");
  const [timeSpeedText, setTimeSpeedText] = useState<Record<keyof TimeSpeedInputs, string>>(
    saved?.timeSpeedText ?? timeSpeedTextFromInputs(defaultTimeSpeedInputs, saved?.unitSystem ?? "metric", saved?.language ?? "de")
  );
  const [energyInputs, setEnergyInputs] = useState<EnergyInputs>(saved?.energyInputs ?? defaultEnergyInputs);
  const [energyText, setEnergyText] = useState<Record<keyof EnergyInputs, string>>(
    saved?.energyText ?? energyTextFromInputs(defaultEnergyInputs, saved?.unitSystem ?? "metric", saved?.language ?? "de")
  );
  const [isModuleDropdownOpen, setIsModuleDropdownOpen] = useState(false);
  const [isMartinEasterEggOpen, setIsMartinEasterEggOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const t = copy[language];
  const activeModuleIndex = navItems.findIndex((item) => item.key === activeModule);
  const activeModuleNumber = String(activeModuleIndex + 1).padStart(2, "0");

  useEffect(() => {
    document.documentElement.dataset.theme =
      visualTheme === "m3Light" ? "m3-light" : visualTheme === "m3Dark" ? "m3-dark" : "jarvis";

    return () => {
      delete document.documentElement.dataset.theme;
    };
  }, [visualTheme]);

  useEffect(() => {
    function openMartinEasterEgg() {
      setIsMartinEasterEggOpen(true);
    }

    function closeOnEscape(event: KeyboardEvent) {
      if (event.key === "Escape") setIsMartinEasterEggOpen(false);
    }

    window.addEventListener("martin-easter-egg", openMartinEasterEgg);
    window.addEventListener("keydown", closeOnEscape);

    return () => {
      window.removeEventListener("martin-easter-egg", openMartinEasterEgg);
      window.removeEventListener("keydown", closeOnEscape);
    };
  }, []);

  useEffect(() => {
    const state: SavedState = {
      version: 1,
      activeModule,
      language,
      unitSystem,
      visualTheme,
      mode,
      inputs,
      inputText,
      wireConversionInputs,
      wireConversionText,
      geometryMode,
      geometryInputs,
      geometryText,
      sheetThicknessInputs,
      sheetThicknessText,
      wireMaterialInputs,
      wireMaterialText,
      timeSpeedMode,
      timeSpeedInputs,
      timeSpeedText,
      energyInputs,
      energyText
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  }, [
    activeModule,
    language,
    unitSystem,
    visualTheme,
    mode,
    inputs,
    inputText,
    wireConversionInputs,
    wireConversionText,
    geometryInputs,
    geometryMode,
    geometryText,
    sheetThicknessInputs,
    sheetThicknessText,
    wireMaterialInputs,
    wireMaterialText,
    timeSpeedMode,
    timeSpeedInputs,
    timeSpeedText,
    energyInputs,
    energyText
  ]);

  function setUnitSystem(nextUnitSystem: UnitSystem) {
    setInputText(textFromInputs(inputs, nextUnitSystem, language));
    setWireConversionText(wireTextFromInputs(wireConversionInputs, nextUnitSystem, language));
    setGeometryText(geometryTextFromInputs(geometryInputs, nextUnitSystem, language));
    setSheetThicknessText(sheetThicknessTextFromInputs(sheetThicknessInputs, nextUnitSystem, language));
    setWireMaterialText(wireMaterialTextFromInputs(wireMaterialInputs, nextUnitSystem, language));
    setTimeSpeedText(timeSpeedTextFromInputs(timeSpeedInputs, nextUnitSystem, language));
    setEnergyText(energyTextFromInputs(energyInputs, nextUnitSystem, language));
    setUnitSystemState(nextUnitSystem);
  }

  function setAppLanguage(nextLanguage: Language) {
    setInputText(textFromInputs(inputs, unitSystem, nextLanguage));
    setWireConversionText(wireTextFromInputs(wireConversionInputs, unitSystem, nextLanguage));
    setGeometryText(geometryTextFromInputs(geometryInputs, unitSystem, nextLanguage));
    setSheetThicknessText(sheetThicknessTextFromInputs(sheetThicknessInputs, unitSystem, nextLanguage));
    setWireMaterialText(wireMaterialTextFromInputs(wireMaterialInputs, unitSystem, nextLanguage));
    setTimeSpeedText(timeSpeedTextFromInputs(timeSpeedInputs, unitSystem, nextLanguage));
    setEnergyText(energyTextFromInputs(energyInputs, unitSystem, nextLanguage));
    setLanguage(nextLanguage);
  }

  function updateInputText(key: keyof WeldParameterInputs, value: string) {
    const normalizedValue = normalizeInputText(value, language);
    setInputText((current) => ({ ...current, [key]: normalizedValue }));
    const parsed = parseNumberInput(normalizedValue);
    if (parsed === null) return;

    setInputs((current) => ({
      ...current,
      [key]: fromDisplayValue(key, parsed, unitSystem)
    }));
  }

  function updateWireConversionText(key: keyof WireConversionInputs, value: string) {
    const normalizedValue = normalizeInputText(value, language);
    setWireConversionText((current) => ({ ...current, [key]: normalizedValue }));
    const parsed = parseNumberInput(normalizedValue);
    if (parsed === null) return;

    const metricKey = key === "sourceWireFeed" ? "wireFeed" : "wireDiameter";
    setWireConversionInputs((current) => ({
      ...current,
      [key]: fromDisplayValue(metricKey, parsed, unitSystem)
    }));
  }

  function updateGeometryText(key: keyof GeometryInputs, value: string) {
    const normalizedValue = normalizeInputText(value, language);
    const parsed = parseNumberInput(normalizedValue);
    if (parsed === null) {
      setGeometryText((current) => ({ ...current, [key]: normalizedValue }));
      return;
    }

    const metricKey = key === "angle" ? "angle" : "aMass";
    const metricValue = key === "angle" ? clampGeometryAngle(parsed) : fromDisplayValue(metricKey, parsed, unitSystem);
    const nextText = key === "angle" ? String(Number(metricValue.toFixed(0))) : normalizedValue;

    setGeometryText((current) => ({ ...current, [key]: nextText }));
    setGeometryInputs((current) => ({
      ...current,
      [key]: metricValue
    }));
  }

  function updateGeometryMode(nextMode: GeometryMode, calculatedKey?: keyof GeometryInputs, value?: number) {
    if (calculatedKey && typeof value === "number" && Number.isFinite(value)) {
      const nextInputs = { ...geometryInputs, [calculatedKey]: value };
      setGeometryInputs(nextInputs);
      setGeometryText(geometryTextFromInputs(nextInputs, unitSystem, language));
    } else {
      const committed = calculateGeometry(geometryMode, geometryInputs);
      const calculated = geometryMode === "zMeasure" ? { zMeasure: committed.zMeasure } : { aMass: committed.aMass };
      const nextInputs = { ...geometryInputs, ...calculated };
      setGeometryInputs(nextInputs);
      setGeometryText(geometryTextFromInputs(nextInputs, unitSystem, language));
    }
    setGeometryMode(nextMode);
  }

  function updateSheetThicknessText(key: keyof SheetThicknessInputs, value: string) {
    const normalizedValue = normalizeInputText(value, language);
    setSheetThicknessText((current) => ({ ...current, [key]: normalizedValue }));
    const parsed = parseNumberInput(normalizedValue);
    if (parsed === null) return;

    setSheetThicknessInputs((current) => ({
      ...current,
      [key]: fromDisplayValue("aMass", parsed, unitSystem)
    }));
  }

  function updateWireMaterialText(key: keyof WireMaterialInputs, value: string) {
    const normalizedValue = normalizeInputText(value, language);
    setWireMaterialText((current) => ({ ...current, [key]: normalizedValue }));
    const parsed = parseNumberInput(normalizedValue);
    if (parsed === null) return;

    let metricValue = parsed;
    if (key === "spoolWeight") metricValue = metricWeightKg(parsed, unitSystem);
    if (key === "wireDiameter") metricValue = fromDisplayValue("wireDiameter", parsed, unitSystem);
    if (key === "wireFeed") metricValue = fromDisplayValue("wireFeed", parsed, unitSystem);

    setWireMaterialInputs((current) => ({
      ...current,
      [key]: metricValue
    }));
  }

  function updateTimeSpeedText(key: keyof TimeSpeedInputs, value: string) {
    const normalizedValue = normalizeInputText(value, language);
    setTimeSpeedText((current) => ({ ...current, [key]: normalizedValue }));
    const parsed = parseNumberInput(normalizedValue);
    if (parsed === null) return;

    let metricValue = parsed;
    if (key === "weldLength") metricValue = metricLengthCm(parsed, unitSystem);
    if (key === "travelSpeed") metricValue = fromDisplayValue("travelSpeed", parsed, unitSystem);

    setTimeSpeedInputs((current) => ({
      ...current,
      [key]: metricValue
    }));
  }

  function updateTimeSpeedMode(nextMode: TimeSpeedMode, calculatedKey?: keyof TimeSpeedInputs, value?: number) {
    if (calculatedKey && typeof value === "number" && Number.isFinite(value)) {
      const nextInputs = { ...timeSpeedInputs, [calculatedKey]: value };
      setTimeSpeedInputs(nextInputs);
      setTimeSpeedText((current) => ({
        ...current,
        [calculatedKey]: timeSpeedInputText(calculatedKey, value, unitSystem, language)
      }));
    } else {
      const committed = calculateTimeSpeed(timeSpeedMode, timeSpeedInputs);
      const calculated = timeSpeedMode === "travelSpeed" ? { travelSpeed: committed.travelSpeed } : { duration: committed.duration };
      const nextInputs = { ...timeSpeedInputs, ...calculated };
      setTimeSpeedInputs(nextInputs);
      setTimeSpeedText(timeSpeedTextFromInputs(nextInputs, unitSystem, language));
    }
    setTimeSpeedMode(nextMode);
  }

  function updateEnergyText(key: keyof EnergyInputs, value: string) {
    const normalizedValue = normalizeInputText(value, language);
    setEnergyText((current) => ({ ...current, [key]: normalizedValue }));
    const parsed = parseNumberInput(normalizedValue);
    if (parsed === null) return;

    let metricValue = parsed;
    if (key === "weldLength") metricValue = metricLengthCm(parsed, unitSystem);
    if (key === "travelSpeed") metricValue = fromDisplayValue("travelSpeed", parsed, unitSystem);

    setEnergyInputs((current) => ({
      ...current,
      [key]: metricValue
    }));
  }

  function updateMode(nextMode: CalculationMode, calculatedKey?: keyof WeldParameterInputs, value?: number) {
    if (calculatedKey && typeof value === "number" && Number.isFinite(value)) {
      const nextInputs = { ...inputs, [calculatedKey]: value };
      setInputs(nextInputs);
      setInputText((current) => ({
        ...current,
        [calculatedKey]: weldInputText(calculatedKey, value, unitSystem, language)
      }));
    }
    setMode(nextMode);
  }

  function exportSettings() {
    const payload: SavedState = {
      version: 1,
      activeModule,
      language,
      unitSystem,
      visualTheme,
      mode,
      inputs,
      inputText,
      wireConversionInputs,
      wireConversionText,
      geometryMode,
      geometryInputs,
      geometryText,
      sheetThicknessInputs,
      sheetThicknessText,
      wireMaterialInputs,
      wireMaterialText,
      timeSpeedMode,
      timeSpeedInputs,
      timeSpeedText,
      energyInputs,
      energyText
    };
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `jules-tools-parameter-${new Date().toISOString().slice(0, 10)}.json`;
    link.click();
    URL.revokeObjectURL(url);
  }

  async function importSettings(file: File) {
    const text = await file.text();
    const parsed = JSON.parse(text) as Partial<SavedState>;
    if (!isLanguage(parsed.language) || !isUnitSystem(parsed.unitSystem) || !isMode(parsed.mode)) {
      throw new Error("Invalid settings file");
    }

    const importedInputs = { ...defaultInputs, ...parsed.inputs };
    const importedWireConversionInputs = { ...defaultWireConversionInputs, ...parsed.wireConversionInputs };
    const importedGeometryInputs = normalizeGeometryInputs({ ...defaultGeometryInputs, ...parsed.geometryInputs });
    const importedSheetThicknessInputs = { ...defaultSheetThicknessInputs, ...parsed.sheetThicknessInputs };
    const importedWireMaterialInputs = { ...defaultWireMaterialInputs, ...parsed.wireMaterialInputs };
    const importedTimeSpeedInputs = { ...defaultTimeSpeedInputs, ...parsed.timeSpeedInputs };
    const importedEnergyInputs = { ...defaultEnergyInputs, ...parsed.energyInputs };
    setLanguage(parsed.language);
    setUnitSystemState(parsed.unitSystem);
    setVisualTheme(normalizeVisualTheme(parsed.visualTheme));
    setActiveModule(isModuleKey(parsed.activeModule) ? parsed.activeModule : "weld");
    setMode(parsed.mode);
    setGeometryMode(parsed.geometryMode === "aMass" || parsed.geometryMode === "zMeasure" ? parsed.geometryMode : "zMeasure");
    setInputs(importedInputs);
    setInputText({
      ...textFromInputs(importedInputs, parsed.unitSystem, parsed.language),
      ...normalizeTextValues<Record<keyof WeldParameterInputs, string>>(parsed.inputText, parsed.language)
    });
    setWireConversionInputs(importedWireConversionInputs);
    setWireConversionText({
      ...wireTextFromInputs(importedWireConversionInputs, parsed.unitSystem, parsed.language),
      ...normalizeTextValues<Record<keyof WireConversionInputs, string>>(parsed.wireConversionText, parsed.language)
    });
    setGeometryInputs(importedGeometryInputs);
    setGeometryText({
      ...geometryTextFromInputs(importedGeometryInputs, parsed.unitSystem, parsed.language),
      ...normalizeTextValues<Record<keyof GeometryInputs, string>>(parsed.geometryText, parsed.language),
      angle: String(Number(importedGeometryInputs.angle.toFixed(0)))
    });
    setSheetThicknessInputs(importedSheetThicknessInputs);
    setSheetThicknessText({
      ...sheetThicknessTextFromInputs(importedSheetThicknessInputs, parsed.unitSystem, parsed.language),
      ...normalizeTextValues<Record<keyof SheetThicknessInputs, string>>(parsed.sheetThicknessText, parsed.language)
    });
    setWireMaterialInputs(importedWireMaterialInputs);
    setWireMaterialText({
      ...wireMaterialTextFromInputs(importedWireMaterialInputs, parsed.unitSystem, parsed.language),
      ...normalizeTextValues<Record<keyof WireMaterialInputs, string>>(parsed.wireMaterialText, parsed.language)
    });
    setTimeSpeedMode(parsed.timeSpeedMode === "duration" || parsed.timeSpeedMode === "travelSpeed" ? parsed.timeSpeedMode : "travelSpeed");
    setTimeSpeedInputs(importedTimeSpeedInputs);
    setTimeSpeedText({
      ...timeSpeedTextFromInputs(importedTimeSpeedInputs, parsed.unitSystem, parsed.language),
      ...normalizeTextValues<Record<keyof TimeSpeedInputs, string>>(parsed.timeSpeedText, parsed.language)
    });
    setEnergyInputs(importedEnergyInputs);
    setEnergyText({
      ...energyTextFromInputs(importedEnergyInputs, parsed.unitSystem, parsed.language),
      ...normalizeTextValues<Record<keyof EnergyInputs, string>>(parsed.energyText, parsed.language)
    });
  }

  return (
    <>
      <main className={`app-shell theme-${visualTheme}`}>
      <section className="topbar" aria-label="Projektübersicht">
        <div className="brand-lockup">
          <span className="brand-chip">Jules Tools</span>
          <h1>{t.appTitle}</h1>
          <span className="header-module">
            {t.module} {activeModuleNumber} / {t.modules[activeModule]}
          </span>
        </div>
        <div className="system-status" aria-label="Systemstatus">
          <span className="status-dot" />
          <span>{t.ready}</span>
        </div>
      </section>

      <section className="hero-grid">
        <aside className="module-menu" aria-label="Module">
          <div className="panel-kicker">
            <SlidersHorizontal size={16} />
            {t.module} {activeModuleNumber}
          </div>

          <div className={isModuleDropdownOpen ? "module-dropdown-shell is-open" : "module-dropdown-shell"}>
            <span>{t.module}</span>
            <div className="module-select-wrap">
              <button
                type="button"
                className="module-dropdown-trigger"
                aria-expanded={isModuleDropdownOpen}
                aria-haspopup="listbox"
                onClick={() => setIsModuleDropdownOpen((current) => !current)}
              >
                <span>{activeModuleNumber} / {t.modules[activeModule]}</span>
                <ChevronDown size={18} aria-hidden="true" />
              </button>
              {isModuleDropdownOpen ? (
                <div className="module-dropdown-list" role="listbox" aria-label={t.module}>
                  {navItems.map((item, index) => {
                    const Icon = item.icon;
                    const isActive = item.key === activeModule;

                    return (
                      <button
                        key={item.key}
                        type="button"
                        className={isActive ? "module-dropdown-option is-active" : "module-dropdown-option"}
                        role="option"
                        aria-selected={isActive}
                        disabled={!item.enabled}
                        onClick={() => {
                          if (item.enabled) {
                            setActiveModule(item.key);
                            setIsModuleDropdownOpen(false);
                          }
                        }}
                      >
                        <span className="module-index">{String(index + 1).padStart(2, "0")}</span>
                        <Icon size={17} />
                        <strong>{t.modules[item.key]}</strong>
                      </button>
                    );
                  })}
                  <div className="module-dropdown-scroll-hint" aria-hidden="true">
                    <ChevronDown size={16} />
                  </div>
                </div>
              ) : null}
            </div>
          </div>

          <div className="module-scroll-shell">
            <nav className="module-list">
              {navItems.map((item, index) => {
                const Icon = item.icon;
                const isActive = item.key === activeModule;

                return (
                  <button
                    key={item.key}
                    type="button"
                    className={isActive ? "module-item is-active" : "module-item"}
                    disabled={!item.enabled}
                    aria-current={isActive ? "page" : undefined}
                    onPointerDown={() => {
                      if (item.enabled) setActiveModule(item.key);
                    }}
                    onClick={() => {
                      if (item.enabled) setActiveModule(item.key);
                    }}
                  >
                    <span className="module-index">{String(index + 1).padStart(2, "0")}</span>
                    <Icon size={18} />
                    <span>
                      <strong>{t.modules[item.key]}</strong>
                    </span>
                  </button>
                );
              })}
            </nav>
          </div>

          <div className="preferences-panel">
            <p className="eyebrow">{t.preferences}</p>

            <div className="toggle-group" aria-label={t.language}>
              <button
                type="button"
                className={language === "de" ? "toggle-button is-active" : "toggle-button"}
                onClick={() => setAppLanguage("de")}
              >
                DE
              </button>
              <button
                type="button"
                className={language === "en" ? "toggle-button is-active" : "toggle-button"}
                onClick={() => setAppLanguage("en")}
              >
                EN
              </button>
            </div>

            <div className="toggle-group" aria-label={t.units}>
              <button
                type="button"
                className={unitSystem === "metric" ? "toggle-button is-active" : "toggle-button"}
                onClick={() => setUnitSystem("metric")}
              >
                Metric
              </button>
              <button
                type="button"
                className={unitSystem === "imperial" ? "toggle-button is-active" : "toggle-button"}
                onClick={() => setUnitSystem("imperial")}
              >
                Imperial
              </button>
            </div>

            <fieldset className="theme-choice-field" aria-label={t.theme}>
              <legend>{t.theme}</legend>
              <div className="theme-choice-control">
                {(["jarvis", "m3Light", "m3Dark"] as const).map((theme) => (
                  <button
                    key={theme}
                    type="button"
                    className={visualTheme === theme ? "theme-choice-option is-active" : "theme-choice-option"}
                    aria-pressed={visualTheme === theme}
                    onClick={() => setVisualTheme(theme)}
                  >
                    {t.themes[theme]}
                  </button>
                ))}
              </div>
            </fieldset>

            <div className="toggle-group transfer-group" aria-label="Parameter transfer">
              <button type="button" className="toggle-button" onClick={exportSettings}>
                <Download size={15} /> {t.exportSettings}
              </button>
              <button type="button" className="toggle-button" onClick={() => fileInputRef.current?.click()}>
                <Upload size={15} /> {t.importSettings}
              </button>
            </div>
            <input
              ref={fileInputRef}
              className="hidden-file-input"
              type="file"
              accept="application/json,.json"
              onChange={(event) => {
                const file = event.target.files?.[0];
                if (file) {
                  importSettings(file).catch(() => window.alert("Import fehlgeschlagen."));
                }
                event.target.value = "";
              }}
            />
          </div>
        </aside>

        {activeModule === "wireConversion" ? (
          <WireConversionCalculator
            language={language}
            unitSystem={unitSystem}
            inputs={wireConversionInputs}
            inputText={wireConversionText}
            onInputTextChange={updateWireConversionText}
          />
        ) : activeModule === "geometry" ? (
          <GeometryCalculator
            language={language}
            unitSystem={unitSystem}
            inputs={geometryInputs}
            mode={geometryMode}
            inputText={geometryText}
            onModeChange={updateGeometryMode}
            onInputTextChange={updateGeometryText}
          />
        ) : activeModule === "sheetThickness" ? (
          <SheetThicknessCalculator
            language={language}
            unitSystem={unitSystem}
            inputs={sheetThicknessInputs}
            inputText={sheetThicknessText}
            onInputTextChange={updateSheetThicknessText}
          />
        ) : activeModule === "wireMaterial" ? (
          <WireMaterialCalculator
            language={language}
            unitSystem={unitSystem}
            inputs={wireMaterialInputs}
            inputText={wireMaterialText}
            onInputTextChange={updateWireMaterialText}
          />
        ) : activeModule === "deposition" ? (
          <DepositionCalculator
            language={language}
            unitSystem={unitSystem}
            inputs={wireMaterialInputs}
            inputText={wireMaterialText}
            onInputTextChange={updateWireMaterialText}
          />
        ) : activeModule === "timeSpeed" ? (
          <TimeSpeedCalculator
            language={language}
            unitSystem={unitSystem}
            mode={timeSpeedMode}
            inputs={timeSpeedInputs}
            inputText={timeSpeedText}
            onModeChange={updateTimeSpeedMode}
            onInputTextChange={updateTimeSpeedText}
          />
        ) : activeModule === "energy" ? (
          <EnergyCalculator
            language={language}
            unitSystem={unitSystem}
            inputs={energyInputs}
            inputText={energyText}
            onInputTextChange={updateEnergyText}
          />
        ) : (
          <WeldParameterCalculator
            language={language}
            unitSystem={unitSystem}
            mode={mode}
            inputs={inputs}
            inputText={inputText}
            onModeChange={updateMode}
            onInputTextChange={updateInputText}
          />
        )}
      </section>
      </main>

      {isMartinEasterEggOpen ? (
        <div
          className="easter-egg-overlay"
          role="dialog"
          aria-modal="true"
          aria-label="Martin Easter Egg"
          onClick={() => setIsMartinEasterEggOpen(false)}
        >
          <div className="easter-egg-stage" onClick={(event) => event.stopPropagation()}>
            <button
              type="button"
              className="easter-egg-close"
              aria-label="Close"
              onClick={() => setIsMartinEasterEggOpen(false)}
            >
              <X size={24} aria-hidden="true" />
            </button>
            <img src="/martin-easter-egg.jpeg" alt="Martin Easter Egg" />
          </div>
        </div>
      ) : null}
    </>
  );
}
